import { Button } from "@comps/ui/button";
import { useConfirmationDialog } from "@comps/ui/confirmation-dialog";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import ObjectActionDto from "@dto/ObjectAction";
import { getDarkColor } from "@utils/colors";
import { canExecuteObjectAction, executeObjectAction, getSelectedRecordKeys } from "@utils/data";
import { CIcon } from "@utils/icons";
import { TW_ListItemIcon, getWsButtonStyle, useHover } from "@utils/twClasses";
import { useState } from "react";
import { useDataStore, useModelStore } from "src/store";

interface ObjectActionButtonProps {
    action: ObjectActionDto;
    className?: string;
    disabled?: boolean;
}

function ObjectActionButton({ action, className, disabled = false }: ObjectActionButtonProps) {
    const wsColor = useModelStore(state => state.wsColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const workspace = useModelStore(state => state.selectedWorkspace);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const rowSelection = useDataStore(state => state.rowSelection);
    const metaLines = useDataStore(state => state.metaLines);
    const fetchData = useDataStore(state => state.fetchData);
    const loadData = useDataStore(state => state.loadData);
    const stopLoadingData = useDataStore(state => state.stopLoadingData);
    const { t } = useTranslation();
    const confirmDialog = useConfirmationDialog();
    const [loading, setLoading] = useState(false);

    let buttonColor = action.color ?? wsColor;
    let buttonDarkColor = action.color ? getDarkColor(action.color) : wsDarkColor;
    let buttonStyle = useHover(getWsButtonStyle(buttonColor), getWsButtonStyle(buttonDarkColor));

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!workspace || !selectedObjectInfos?.object) return;

        // Without selected record, only an action always visible can run
        const objectKey = selectedObjectInfos.object._key;
        const recordKeys = getSelectedRecordKeys(rowSelection, metaLines, objectKey);
        if (!canExecuteObjectAction(action, recordKeys)) return;

        if (action.confirmationMsg) {
            const confirmed = await confirmDialog({
                title: t("field.button.confirm.dialog.title"),
                description: action.confirmationMsg.replace(/\\n/g, '\n'),
                cancelButtonStyle: getWsButtonStyle(buttonColor),
                cancelButtonHoverStyle: getWsButtonStyle(buttonDarkColor),
                confirmButtonStyle: getWsButtonStyle(buttonColor),
                confirmButtonHoverStyle: getWsButtonStyle(buttonDarkColor),
            });
            if (!confirmed) return;
        }

        setLoading(true);
        loadData();
        try {
            await executeObjectAction(workspace._key, objectKey, action, recordKeys, () => {
                if (action.refresh) {
                    loadData();
                    fetchData().finally(() => {
                        stopLoadingData();
                    });
                }
            });
        } finally {
            setLoading(false);
            stopLoadingData();
        }
    };

    if (action.description) {
        return (
            <TooltipProvider>
                <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                        <Button className={"px-2 py-1 text-sm flex flex-row justify-start items-center gap-2 h-8 text-ellipsis whitespace-nowrap overflow-hidden" + (className ? " " + className : "")} 
                            onClick={handleClick} 
                            disabled={disabled || loading} 
                            {...buttonStyle} 
                        >
                            {action.icon && <CIcon compozzIcon={action.icon} className={`${TW_ListItemIcon}`} />}
                            {action.label}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {action.description}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    } else {
    return (
        <Button
            className={"px-3 py-1 text-sm flex flex-row justify-start items-center gap-2 h-8 text-ellipsis whitespace-nowrap overflow-hidden" + (className ? " " + className : "")}
            onClick={handleClick}
            disabled={disabled || loading}
            {...buttonStyle}
        >
            {action.icon && <CIcon compozzIcon={action.icon} className={`${TW_ListItemIcon}`} />}
            {action.label}
        </Button>
    );
}
}

export default ObjectActionButton;
